import React, {useEffect, useState, FC, useRef, createRef} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "next-i18next";
import dynamic from "next/dynamic";

const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});

import {ApexOptions} from "apexcharts";
import fr from "apexcharts/dist/locales/fr.json";
import en from "apexcharts/dist/locales/en.json";
import es from "apexcharts/dist/locales/es.json";

interface TripTemperatureProps {
    tripId: string;
}

interface TempReading {
    max: number,
    avg: number,
    min: number
}

const TripTemperatureChart: FC<TripTemperatureProps> = ({tripId}) => {
    const {t, i18n} = useTranslation();

    const chartWrapperRef = useRef<HTMLDivElement>(null);


    const [tempData, setTempData] = useState([] as TempReading[]);

    const [fetching, setFetching] = useState(false)

    useEffect(() => {

        if (chartWrapperRef.current && fetching) {
            const fetchData = async () => {
                try {
                    console.log("loading temp Data ")

                    const response = await fetch(
                        `/content/temperature-data/${tripId.replace("_members", "")}_temperatures.json`
                    );
                    setTempData(await response.json());
                } catch (e) {
                    console.error(e);
                }
            };
            fetchData()
            setFetching(false)
        }

    }, [fetching])

    useEffect(() => {

        const handleScroll = (e: any) => {
            // const body = document.body,
            //     html = document.documentElement

            // const height = Math.max(body.scrollHeight, body.offsetHeight,
            //     html.clientHeight, html.scrollHeight, html.offsetHeight)

            const scrollTop = Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)

            // // console.log('height', height)
            // console.log('scrollTop', scrollTop)
            //
            // console.log(chartWrapperRef.current?.offsetTop)

            if (chartWrapperRef.current && scrollTop > chartWrapperRef.current?.offsetTop - 50 - chartWrapperRef.current?.clientHeight) {
                // console.log("setting fetching true")
                setFetching(true)
                window.removeEventListener("scroll", handleScroll)
            }

        }

        window.addEventListener("scroll", handleScroll, {passive: true})

        return () => {
            window.removeEventListener("scroll", handleScroll)
        }

    }, [])


    const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(
        (i) => new Date(2000, i, 2).getTime()
        // formatDefault(new Date(2000, i, 1), "MMM")
    );

    const series: ApexAxisChartSeries = [
        // labels: months,
        // datasets: []

        {
            name: "Max",
            data: tempData.map((val, idx) => {
                return {
                    y: val.max,
                    x: months[idx],
                };
            }),
        },
        {
            name: t("avg"),
            data: tempData.map((val, idx) => {
                return {
                    y: val.avg,
                    x: months[idx],
                };
            }),
        },
        {
            name: "Min",
            data: tempData.map((val, idx) => {
                return {
                    y: val.min,
                    x: months[idx],
                };
            }),
        },
        {
            name: "hack",
            data: tempData.map((val, idx) => {
                return {
                    y: null,
                    x: months[idx],
                };
            }),
        },
    ];

    const options: ApexOptions = {
        grid: {
            padding: {
                right: -20,
                left: -20,
                bottom: 30,
            },
            xaxis: {
                lines: {
                    show: true
                }
            },
        },
        chart: {
            animations: {
                enabled: true,
                dynamicAnimation: {
                    enabled: true
                }
            },
            selection: {
                enabled: false,
            },
            zoom: {
                enabled: false,
            },
            locales: [en, fr, es],
            defaultLocale: i18n.language,
            toolbar: {
                show: false,
            },
        },
        tooltip: {
            enabled: true,
            shared: true,
            enabledOnSeries: [0, 1, 2],
            x: {
                format: "MMMM",
            },
            y: {
                formatter(
                    val: number,
                    {seriesN, seriesIndex, dataPointIndex, w}
                ): string {
                    const celsius = Math.round(val * 10) / 10;
                    const fahrenheit = Math.round(celsius * (9 / 5) + 32);
                    return `${celsius}°C/${fahrenheit}°F`;
                },
            },
            style: {
                fontSize: "14px",
                fontFamily: "Sana Sans Alt, sans-serif",
            },
        },
        markers: {
            size: 4,
            hover: {
                sizeOffset: 1,
            },
        },
        title: {
            text: t("sea-surface-temps"),
            align: "center",
            style: {
                fontSize: "18px",
                fontFamily: "Sana Sans Alt, sans-serif",
                fontWeight: "normal",
            },
        },
        legend: {
            position: "top",
            show: true,
            showForNullSeries: false,
            showForZeroSeries: false,
        },
        stroke: {
            curve: "smooth",
            width: 2,
        },
        dataLabels: {
            enabled: false,
        },
        colors: ["#e15356", "#8f8f8f", "#1b9ffb"],
        xaxis: {
            // categories: months,

            tooltip: {
                enabled: false,
            },
            type: "datetime",
            labels: {
                rotateAlways: true,
                hideOverlappingLabels: false,
                format: "MMM",
                style: {
                    fontFamily: "Sana Sans Alt, sans-serif",
                    fontWeight: "normal",
                },
            },
        },
        yaxis: [
            {
                labels: {
                    offsetX: -26,
                },
                min: 18,
                max: 32,
                tickAmount: 8,
                decimalsInFloat: 0,
                forceNiceScale: true,

                axisTicks: {
                    show: false,
                },
                axisBorder: {
                    show: false,
                    color: "#008FFB",
                },
                title: {
                    text: "°Celsius",
                    style: {
                        color: "#616161",
                        fontSize: "14px",
                        fontFamily: "Sana Sans Alt, sans-serif",
                        fontWeight: "normal",
                    },
                },
                tooltip: {
                    enabled: false,
                },
            },
            {
                min: 18,
                max: 32,
                show: false,
            },
            {
                min: 18,
                max: 32,
                show: false,
            },
            {
                labels: {
                    offsetX: -24,
                },
                min: 64,
                max: 90,
                tickAmount: 6,
                showAlways: true,
                showForNullSeries: true,
                decimalsInFloat: 0,
                opposite: true,
                tooltip: {
                    enabled: false,
                },
                axisTicks: {
                    show: false,
                },
                axisBorder: {
                    show: false,
                },
                title: {
                    text: "°Fahrenheit",
                    style: {
                        color: "#616161",
                        fontSize: "14px",
                        fontFamily: "Sana Sans Alt, sans-serif",
                        fontWeight: "normal",
                    },
                },
            },
        ],
    };


    return (
        <div style={{minHeight: 300}} ref={chartWrapperRef}>
            {tempData.length > 0 && (
                <Chart series={series} options={options} type='line' height={300}/>
            )}
        </div>
    );
};

TripTemperatureChart.propTypes = {
    tripId: PropTypes.string.isRequired,
};

export default TripTemperatureChart;
